import { useRef, useState } from 'preact/hooks';
import { Button } from '../../components/ui/Button.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { SupportLink } from '../../components/ui/SupportLink.js';
import { useAuth } from '../../context/AuthContext.js';
import { api } from '../../services/api.js';
import { PHONE_RE } from '../../utils/validation.js';
import { NAME_IC_HINT, NAME_IC_HINT_ID } from './nameHint.js';

interface ProfileEditPageProps {
  path?: string;
}

// 140: moved to `utils/validation.ts` so `RegisterPage` uses the same one rather than a second copy.
// Mirror of the backend rule (shared/fieldSchemas.ts): optional +, then digits/space/dash.
const AVATAR_MAX_BYTES = 5 * 1024 * 1024;

export function ProfileEditPage(_props: ProfileEditPageProps) {
  const { user, refreshUser } = useAuth();
  const fileRef = useRef<HTMLInputElement>(null);
  const [firstName, setFirstName] = useState(user?.first_name ?? '');
  const [lastName, setLastName] = useState(user?.last_name ?? '');
  const [phone, setPhone] = useState(user?.phone ?? '');
  const [phoneError, setPhoneError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [avatarError, setAvatarError] = useState('');
  const [avatarBusy, setAvatarBusy] = useState(false);

  if (!user) return null;

  async function handleSave() {
    if (!user) return;
    setSaved(false);
    setSaveError('');
    if (phone !== '' && !PHONE_RE.test(phone)) {
      setPhoneError('Invalid phone number');
      return;
    }
    setPhoneError('');

    const body: Record<string, string> = {};
    if (firstName !== (user.first_name ?? '')) body.first_name = firstName;
    if (lastName !== (user.last_name ?? '')) body.last_name = lastName;
    if (phone !== (user.phone ?? '')) body.phone = phone;

    setSaving(true);
    try {
      await api.patch('/users/me', body);
      await refreshUser();
      setSaved(true);
    } catch {
      setSaveError('Could not save your changes. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  async function handleFile(e: Event) {
    setAvatarError('');
    const file = (e.target as HTMLInputElement).files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setAvatarError('Please choose an image file.');
      return;
    }
    if (file.size > AVATAR_MAX_BYTES) {
      setAvatarError('Image must be 5 MB or smaller.');
      return;
    }
    const form = new FormData();
    form.append('avatar', file);
    setAvatarBusy(true);
    try {
      await api.upload('/users/me/avatar', form);
      await refreshUser();
    } catch {
      setAvatarError('Upload failed. Your photo was not changed.');
    } finally {
      setAvatarBusy(false);
    }
  }

  async function handleRemove() {
    setAvatarError('');
    setAvatarBusy(true);
    try {
      await api.delete('/users/me/avatar');
      await refreshUser();
    } catch {
      setAvatarError('Could not remove your photo. Please try again.');
    } finally {
      setAvatarBusy(false);
    }
  }

  const initials =
    `${firstName} ${lastName}`
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .map((p) => p[0]?.toUpperCase())
      .slice(0, 2)
      .join('') || (user.email[0] ?? '?').toUpperCase();

  return (
    <div className="bg-bg px-4 py-6">
      <PageHeader title="Edit Profile" fallback="/account" />

      <div className="flex items-center gap-4 mb-6">
        {user.avatar_url ? (
          <img src={user.avatar_url} alt="avatar" className="w-20 h-20 rounded-full object-cover" />
        ) : (
          <div className="w-20 h-20 rounded-full bg-surface-mid flex items-center justify-center text-2xl font-bold text-primary">
            {initials}
          </div>
        )}
        <div className="flex flex-col gap-2">
          <Button
            size="sm"
            variant="secondary"
            loading={avatarBusy}
            onClick={() => fileRef.current?.click()}
          >
            {user.avatar_url ? 'Change photo' : 'Upload photo'}
          </Button>
          {user.avatar_url ? (
            <Button size="sm" variant="ghost" loading={avatarBusy} onClick={handleRemove}>
              Remove photo
            </Button>
          ) : null}
          <input
            ref={fileRef}
            data-testid="avatar-input"
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFile}
          />
        </div>
      </div>
      {avatarError ? <p className="text-sm text-red-400 mb-4">{avatarError}</p> : null}

      <div className="flex flex-col gap-4">
        <label className="flex flex-col gap-1">
          <span className="text-sm text-gray-400">First name</span>
          <input
            data-testid="first-name"
            aria-describedby={NAME_IC_HINT_ID}
            value={firstName}
            onInput={(e) => setFirstName((e.target as HTMLInputElement).value)}
            className="bg-surface-card rounded-lg px-3 py-2 text-text"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-sm text-gray-400">Last name</span>
          <input
            data-testid="last-name"
            aria-describedby={NAME_IC_HINT_ID}
            value={lastName}
            onInput={(e) => setLastName((e.target as HTMLInputElement).value)}
            className="bg-surface-card rounded-lg px-3 py-2 text-text"
          />
        </label>
        <p id={NAME_IC_HINT_ID} data-testid="name-ic-hint" className="-mt-2 text-xs text-gray-400">
          {NAME_IC_HINT}
        </p>
        <label className="flex flex-col gap-1">
          <span className="text-sm text-gray-400">Phone</span>
          <input
            data-testid="phone"
            value={phone}
            onInput={(e) => setPhone((e.target as HTMLInputElement).value)}
            className="bg-surface-card rounded-lg px-3 py-2 text-text"
          />
          {phoneError ? <span className="text-sm text-red-400">{phoneError}</span> : null}
        </label>

        {/* 330 (FR-012): the campus is read-only here — chosen once, changed only by Carii. */}
        <div data-testid="profile-campus" className="flex flex-col gap-1">
          <span className="text-sm text-gray-400">Campus</span>
          <span className="rounded-lg bg-surface-card px-3 py-2 text-text">
            {user.campus?.name ?? 'Not chosen'}
          </span>
          <SupportLink
            data-testid="profile-campus-change"
            className="text-sm text-primary underline"
          >
            Ask Carii to change it
          </SupportLink>
        </div>

        <Button loading={saving} onClick={handleSave}>
          Save
        </Button>
        {saveError ? <p className="text-sm text-red-400">{saveError}</p> : null}
        {saved ? <p className="text-sm text-green-400">Profile updated.</p> : null}
      </div>
    </div>
  );
}
